/**
 * Browser-only persistence for the Google ID token.
 *
 * GIS holds no session of ours between page loads, and its silent One Tap
 * restore is slow (it can only be settled by a timeout under FedCM) and often
 * suppressed outright. Without persistence every reload — including a hard
 * refresh mid-shift — dropped the operator back at the sign-in gate.
 *
 * The token is stored as-is and trusted only until its own `exp`. It is not a
 * refresh token: at worst a stolen copy is the same one-hour bearer that
 * already lives in JS memory for every API call, and the gateway still verifies
 * signature, audience, and hosted domain on each use.
 */

const STORAGE_KEY = 'stream-google-id-token'

/**
 * Treat the token as expired this long before its `exp`, so a restored token
 * does not die between the staff-profile check and the first API call.
 */
const EXPIRY_MARGIN_MS = 60_000

/** Read `exp` (seconds) from a JWT payload, or null if it cannot be parsed. */
export function idTokenExpiry(idToken: string): number | null {
  const payload = idToken.split('.')[1]
  if (!payload) return null
  try {
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    const exp = (JSON.parse(json) as { exp?: unknown }).exp
    return typeof exp === 'number' ? exp : null
  } catch {
    return null
  }
}

/** The stored token if one exists and has not expired, otherwise null. */
export function loadIdToken(now: number = Date.now()): string | null {
  let token: string | null
  try {
    token = localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
  if (!token) return null

  const exp = idTokenExpiry(token)
  if (exp === null || exp * 1000 - EXPIRY_MARGIN_MS <= now) {
    clearIdToken()
    return null
  }
  return token
}

export function saveIdToken(idToken: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, idToken)
  } catch {
    // Storage blocked (private mode, site-data policy) — the session still
    // works for this page load, it just will not survive a reload.
  }
}

export function clearIdToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Best-effort.
  }
}
